import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from './config.js';
import axios from "axios";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Box,
  ThemeProvider,
  createTheme,
  CssBaseline,
  TextField,
  InputAdornment,
  Chip,
  useMediaQuery,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack
} from '@mui/material';
import { 
  Description as PdfIcon, 
  Search as SearchIcon,
  SortByAlpha as SortIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// ... rest of your imports and theme
const customTheme = createTheme({
  palette: {
    primary: {
      main: '#1d5576',
    },
    secondary: {
      main: '#f9a91e',
    },
    background: {
      default: '#ffffff',
    }
  },
  typography: {
    h4: {
      fontWeight: 'bold',
    },
    h5: {
      fontWeight: 'bold',
    },
    h6: {
      fontWeight: 'medium',
    }
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});

const MarketingMaterialsPage = () => {
  const [marketingData, setMarketingData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [imageErrors, setImageErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState('default'); // 'default', 'asc', or 'desc'
  const isMobile = useMediaQuery(customTheme.breakpoints.down('sm'));
  const navigate = useNavigate();

  useEffect(() => {
    fetchMarketingData();
  }, []);

  const fetchMarketingData = async () => {
    const token = localStorage.getItem("access"); // use access token
    if (!token) {
      console.error("No access token found. Please login.");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/marketing-materials/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      setMarketingData(response.data);
    } catch (error) {
      console.error("Error fetching marketing data:", error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (brand) => {
    navigate(`/brand/${brand.id}`, { state: { brand } });
  };

  const handleImageError = (itemName) => {
    setImageErrors(prev => ({
      ...prev,
      [itemName]: true
    }));
  };

  const handleSortChange = (event) => {
    setSortOrder(event.target.value);
  };

  // Function to sort items by name
  const sortItemsByName = (items) => {
    if (sortOrder === 'default') {
      return items; // Return original order
    }
    
    return [...items].sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();
      
      if (sortOrder === 'asc') {
        return nameA.localeCompare(nameB);
      } else {
        return nameB.localeCompare(nameA);
      }
    });
  };

  // Filter and sort the data
  const filteredAndSortedData = Object.entries(marketingData).reduce((acc, [category, items]) => {
    // First filter items based on search term
    const filteredItems = items.filter(item =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    // Then sort the filtered items based on selected sort order
    if (filteredItems.length > 0) {
      acc[category] = sortItemsByName(filteredItems);
    }
    
    return acc;
  }, {});

  // Function to render the appropriate icon/image
  const renderIcon = (item) => {
    const hasImageError = imageErrors[item.name];
    const hasImageUrl = item.imageUrl && item.imageUrl.trim() !== '';

    if (hasImageUrl && !hasImageError) {
      return (
        <Box
          component="img"
          src={item.imageUrl}
          alt={`${item.name} logo`}
          sx={{ 
            height: isMobile ? 48 : 58,
            width: 'auto',
            maxWidth: '120px',
            mb: 2,
            objectFit: 'contain'
          }}
          onError={() => handleImageError(item.name)}
        />
      );
    } else {
      return (
        <PdfIcon 
          sx={{ 
            fontSize: isMobile ? 40 : 48,
            color: 'primary.main',
            mb: 2
          }} 
        />
      );
    }
  };

  if (loading) {
    return (
      <ThemeProvider theme={customTheme}>
        <CssBaseline />
        <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={customTheme}>
      <CssBaseline />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header Section */}
        <Box sx={{ 
          mb: 4, 
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Typography 
            variant={isMobile ? "h5" : "h4"}
            component="h1" 
            gutterBottom
            sx={{ 
              color: 'primary.main'
            }}
          >
            Marketing Materials
          </Typography>
          <Typography 
            variant={isMobile ? "body1" : "h6"}
            color="text.secondary"
            sx={{ mb: 3, maxWidth: '600px' }}
          >
            Access all marketing documents and brand materials
          </Typography>
          
          <Stack 
            direction={isMobile ? "column" : "row"} 
            spacing={2} 
            sx={{ 
              width: '100%',
              maxWidth: 600,
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <TextField
              placeholder="Search materials..."
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ 
                flex: 2,
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <FormControl sx={{ minWidth: 140, flex: 1 }}>
              <InputLabel id="sort-select-label">Sort by Name</InputLabel>
              <Select
                labelId="sort-select-label"
                id="sort-select"
                value={sortOrder}
                label="Sort by Name"
                onChange={handleSortChange}
                startAdornment={
                  <InputAdornment position="start">
                    <SortIcon />
                  </InputAdornment>
                }
              >
                <MenuItem value="default">Default</MenuItem>
                <MenuItem value="asc">ASC (A-Z)</MenuItem>
                <MenuItem value="desc">DESC (Z-A)</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>

        {/* Cards Section */}
        {Object.entries(filteredAndSortedData).map(([category, items]) => (
          <Box key={category} sx={{ mb: 6 }}>
            <Typography 
              variant={isMobile ? "h6" : "h5"}
              component="h2" 
              gutterBottom
              sx={{ 
                color: 'secondary.main',
                borderBottom: '2px solid',
                borderColor: 'secondary.main',
                pb: 1,
                mb: 3,
                textAlign: 'left'
              }}
            >
              {category} ({items.length})
            </Typography>
            
            <Grid 
              container 
              spacing={3} 
              sx={{ 
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              {items.map((item) => (
                <Grid 
                  item 
                  xs={12} 
                  sm={8} 
                  md={6} 
                  lg={3} 
                  key={item.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Card 
                    sx={{ 
                      height: '100%',
                      width: '270px',
                      minHeight: 200,
                      maxWidth: '400px',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s ease-in-out',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 6,
                      }
                    }}
                  >
                    <CardActionArea 
                      onClick={() => handleCardClick(item)}
                      sx={{ 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        flexGrow: 1
                      }}
                    >
                      <CardContent sx={{ 
                        textAlign: 'center', 
                        p: isMobile ? 2 : 3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexGrow: 1,
                        width: '100%'
                      }}>
                        {renderIcon(item)}
                        
                        <Typography 
                          variant={isMobile ? "subtitle1" : "h6"}
                          component="h3"
                          sx={{ 
                            fontWeight: 'medium',
                            mb: 1,
                            minHeight: isMobile ? '50px' : '60px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            lineHeight: 1.2,
                            textAlign: 'center'
                          }}
                        >
                          {item.name}
                        </Typography>
                        <Chip 
                          label="View Details"
                          size="small" 
                          variant="outlined"
                          sx={{ 
                            mb: 1,
                            borderColor: 'primary.main',
                            color: 'primary.main'
                          }}
                        />
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}

        {/* No Results Message */}
        {Object.keys(filteredAndSortedData).length === 0 && (
          <Box sx={{ 
            textAlign: 'center', 
            py: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography variant="h6" color="text.secondary">
              No materials found matching your search.
            </Typography>
          </Box>
        )}
      </Container>
    </ThemeProvider>
  );
};

export default MarketingMaterialsPage;